import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import Payment, { FormType } from '@solidgate/react-sdk';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import { Loader } from '../Loader';
import config from '../../../config';
import { savePurchaseSubscription } from '../../../lib/use-cases/get-purchase';
import { paymentErrorMapper } from '../../../lib/mappers/payment-error-mapper';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import sslImageSrc from '../../assets/images/subscription/ssl.png';
import paypalImageSrc from '../../assets/images/subscription/paypal-verified.png';
import visaImageSrc from '../../assets/images/subscription/visa.png';
import visaSecureImageSrc from '../../assets/images/subscription/visa-secure.png';
import mastercardImageSrc from '../../assets/images/subscription/mastercard.png';
import mastercardIdCheckImageSrc from '../../assets/images/subscription/mastercard-idcheck.png';
import styles from './PaymentForm.css';

const cx = classnames.bind(styles);

const initialFormParams = {
    allowSubmit: false,
    isCardHolderVisible: false,
    formTypeClass: FormType.Default,
    googleFontLink: '//fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap',
    cardNumberPlaceholder: 'xxxx xxxx xxxx xxxx',
    cardNumberLabel: 'Card Number',
    cardExpiryDatePlaceholder: 'MM / YY',
    cardExpiryDateLabel: 'Expiry Date',
    cardCvvPlaceholder: 'CVC',
    cardCvvLabel: 'Security Code',
    cardBrands: ['visa', 'mastercard']
};

// const paypalButtonParams = {
//     enabled: true,
//     color: 'gold',
//     label: 'buynow',
//     shape: 'rect',
//     height: 52
// };

const googlePayButtonParams = {
    enabled: true,
    color: 'black',
    height: 52,
    type: 'plain'
};

const applePayButtonParams = {
    enabled: true,
    color: 'black',
    height: 52,
    type: 'plain'
};

const customFormStyles = {
    'form_body': {
        'font-family': 'Inter, sans-serif',
        'width': '100%',
        '.two-columns': {
            'grid-column-gap': '16px',
            'grid-template-columns': 'repeat(2, 1fr)',
            'margin': '8px 0'
        },
        '.form-container': {
            'padding-top': '0px'
        }
    },
    'card_brands': {
        'justify-content': 'center',
        'padding': '0',
        'gap': '8px',
        'i': {
            'width': '41px',
            'height': '31px'
        }
    },
    'input_group': {
        '.label': {
            'color': '#0F172A',
            'font-size': '14px',
            'line-height': '20px',
            'font-weight': '600',
            'margin-bottom': '4px'
        },
        '.input-block input': {
            'padding-left': '16px !important',
            'padding': '18px 14px',
            'height': 'auto',
            'font-size': '16px',
            'line-height': '24px',
            'font-weight': '600',
            'color': '#0F172A',
            'letter-spacing': '0',
            'border-radius': '8px',
            'background-color': '#FFFFFF',
            'border-color': '#4D609A',
            'border-width': '2px',
            'box-shadow': '2px 4px 3px 0 rgba(0, 0, 0, 0.25) inset',
            '::placeholder': {
                'font-size': '16px',
                'font-family': 'Inter, sans-serif',
                'line-height': '24px !important',
                'font-weight': '400',
                'color': '#545F92',
                'letter-spacing': '0px',
                'position': 'static'
            },
            ':focus': {
                'border-color': '#1C59FF'
            }
        },
        '.input-block.error input': {
            'border-color': '#F70000',
            'box-shadow': 'none'
        },
        '.input-block.error .label': {
            'color': '#0F172A'
        },
        '.input-block.not-empty .label': {
            'font-size': '14px',
            'color': '#0F172A'
        },
        '.error-text': {
            'font-size': '12px',
            'font-weight': '400',
            'line-height': '16px',
            'margin-top': '4px',
            'padding': '0',
            'color': '#F70000',
            '.triangle': {
                'display': 'none'
            }
        },
        '.brand-icon': {
            'bottom': '19px',
            'right': '16px',
            'left': 'unset',
            'top': 'unset'
        },
        '.tooltip-icon': {
            'display': 'none'
        }
    }
};

const VISA_BRAND = 'VISA';
const MASTERCARD_BRAND = 'MASTERCARD';
const AMEX_BRAND = 'AMERICAN EXPRESS';
const CARD_BRANDS = [VISA_BRAND, MASTERCARD_BRAND, AMEX_BRAND];

const FAST_PAYMENT_ITEM = 'fastPayment';
const CARD_ITEM = 'card';
const FAILED_PAYMENTS_BLOCK_THRESHOLD = 2;

function PaymentForm({ className, merchantData, setErrorData, successUrl, plan, discount, failedCard, isWalletEnabled }) {
    const classes = cx({
        root: true,
        [className]: Boolean(className)
    });
    const [isMounted, setIsMounted] = useState(false);
    const [isWalletMounted, setIsWalletMounted] = useState(false);
    const [form, setForm] = useState(null);
    // const paypalContainerRef = useRef();
    const googlePayContainerRef = useRef();
    const applePayContainerRef = useRef();
    const [activeItem, setActiveItem] = useState(CARD_ITEM);

    const googlePayParams = { ...googlePayButtonParams, enabled: isWalletEnabled };
    const applePayParams = { ...applePayButtonParams, enabled: isWalletEnabled };

    const changeActiveItem = item => () => {
        setActiveItem(item);
    };

    const handleOnReadyPaymentInstance = useCallback(newForm => {
        setForm(newForm);
    }, []);

    const handleMounted = useCallback((e) => {
        if (e.entity === 'form') {
            setIsMounted(true);
        }

        if (e.entity === 'applebtn' || e.entity === 'googlebtn') {
            setIsWalletMounted(true);
        }
    }, []);

    const [isPending, setIsPending] = useState(false);
    const [card, setCard] = useState({});
    const [errorText, setErrorText] = useState('');

    const isCardBlocked =
        failedCard?.numberOfFailedPayments >= FAILED_PAYMENTS_BLOCK_THRESHOLD &&
        Boolean(card?.bin) &&
        card.bin === failedCard?.mask?.slice(0, 6);

    const handleSubmit = (e) => {
        e.preventDefault();

        logEvent(EVENT_TYPES.USER_PROPERTIES, { 'payment_provider': 'card' });
        logEvent(EVENT_TYPES.SALE_CHECKOUT_CLICK_PAY);

        if (isCardBlocked) {
            return;
        }

        if (card?.brand && (!CARD_BRANDS.includes(card.brand))) {
            return setErrorText('Please use Visa, MasterCard or American Exspress select a different payment method.');
        }

        if (form) {
            form.submit();
        } else {
            console.error('Solidgate Payment ref is not available.');
        }
    };

    const successPay = () => {
        savePurchaseSubscription(merchantData.subscriptionId, plan, discount);

        window.location.replace(successUrl || `${config.platformUrl}/subscription/${merchantData.subscriptionId}/thank-you?is_upsell=false`);
    };

    const showErrorModal = errorCode => {
        setErrorData({ ...paymentErrorMapper.toDomain(errorCode), isModalVisible: true });
    };

    const handleOnError = (e) => {
        console.log(e, 'error');

        setIsPending(true);
        showErrorModal(e.code);
    };

    const handleOnFail = e => {
        console.log(e, 'fail');

        setIsPending(true);
        showErrorModal(e.code);
    };

    const handleOrderStatus = (e) => {
        console.log(e, 'order status');

        if (e.response.order?.status !== '3ds_verify') {
            setIsPending(true);
        }

        if (e.response.error?.code) {
            return showErrorModal(e.response.error.code);
        }

        if (e.response.order?.status === 'auth_ok' || e.response.order.status === 'settle_ok') {
            successPay();
        }
    };

    const handleOnSuccess = () => {
        setIsPending(true);

        successPay();
    };

    const handleOnInteraction = (e) => {
        if (e.target?.type === 'input' && e.target?.interaction === 'change' && errorText) {
            setErrorText('');
        }

        if (e.target?.type === 'button' && e.target?.interaction === 'click') {
            if (e.target.name === 'paypal') {
                logEvent(EVENT_TYPES.USER_PROPERTIES, { 'payment_provider': 'paypal' });
            }

            if (e.target.name === 'applePay') {
                logEvent(EVENT_TYPES.USER_PROPERTIES, { 'payment_provider': 'apple_pay' });
            }

            if (e.target.name === 'googlePay') {
                logEvent(EVENT_TYPES.USER_PROPERTIES, { 'payment_provider': 'google_pay' });
            }

            logEvent(EVENT_TYPES.SALE_CHECKOUT_CLICK_PAY);
        }
    };

    const handleCard = cardData => {
        if (cardData?.card) {
            setCard(cardData.card);
        }
    };

    return (
        <form className={classes} onSubmit={handleSubmit}>
            {/* <div className={cx('accordionItem', 'accordionItemFirst')}>
                <button
                    type='button'
                    className={styles.accordionItemHeader}
                    onClick={changeActiveItem(FAST_PAYMENT_ITEM)}
                    aria-expanded={activeItem === FAST_PAYMENT_ITEM}
                >
                    <span className={cx('checkbox', { active: activeItem === FAST_PAYMENT_ITEM })}>
                        <span className={styles.checkmark} />
                    </span>
                    <span>Fast Payment</span>
                </button>
                <div className={cx('accordionItemContent', { visible: activeItem === FAST_PAYMENT_ITEM })}>
                    <p className={styles.informationText}>The safer, easier way to pay</p>
                    <div className={cx('paymentButtons', { mounted: isMounted })}>
                        <div ref={paypalContainerRef} />
                    </div>
                </div>
            </div>
            {isMounted && (
                <>
                    <p className={styles.paymentDivider}>
                        <span>or pay with a card</span>
                    </p>
                </>
            )} */}
            <div className={cx('paymentButtons', { mounted: isWalletMounted, hidden: !isWalletMounted })}>
                <div ref={applePayContainerRef} />
                <div ref={googlePayContainerRef} />
            </div>
            {isWalletMounted && (
                <p className={styles.paymentDivider}>
                    <span>or pay with a card</span>
                </p>
            )}
            <div className={styles.accordionItem}>
                <button
                    type='button'
                    className={styles.accordionItemHeader}
                    onClick={changeActiveItem(CARD_ITEM)}
                    aria-expanded={activeItem === CARD_ITEM}
                >
                    <span className={cx('checkbox', { active: activeItem === CARD_ITEM })}>
                        <span className={styles.checkmark} />
                    </span>
                    <span>Credit card</span>
                    {activeItem !== CARD_ITEM && (
                        <span className={styles.paymentMethods}>
                            <img src={visaImageSrc} alt='visa' />
                            <img src={mastercardImageSrc} alt='mastercard' />
                        </span>
                    )}
                </button>
                <div className={cx('accordionItemContent', { visible: activeItem === CARD_ITEM })}>
                    {isPending
                        ? <div className={styles.loaderWrapper}><Loader variant='secondary' className={styles.loader} /></div>
                        : (
                            <Payment
                                // paypalButtonParams={paypalButtonParams}
                                // paypalContainerRef={paypalContainerRef}
                                googlePayButtonParams={googlePayParams}
                                googlePayContainerRef={googlePayContainerRef}
                                applePayButtonParams={applePayParams}
                                applePayContainerRef={applePayContainerRef}
                                merchantData={merchantData}
                                styles={customFormStyles}
                                formParams={initialFormParams}
                                onError={handleOnError}
                                onFail={handleOnFail}
                                onCard={handleCard}
                                onOrderStatus={handleOrderStatus}
                                onSuccess={handleOnSuccess}
                                onInteraction={handleOnInteraction}
                                onMounted={handleMounted}
                                onReadyPaymentInstance={handleOnReadyPaymentInstance}
                            />
                        )
                    }
                    {isMounted && (
                        <>
                            <Button
                                label='Subscribe'
                                onClick={handleSubmit}
                                className={styles.button}
                                isLoading={isPending}
                                disabled={isCardBlocked}
                            />
                            {isCardBlocked && (
                                <p className={styles.errorText} role='alert'>
                                    This card has reached the payment attempt limit. Please try a different card.
                                </p>
                            )}
                            {Boolean(errorText) && <p className={styles.errorText} role='alert'>{errorText}</p>}
                        </>
                    )}
                </div>
            </div>
            <ul className={cx('trustBadges', { mounted: isMounted })}>
                <li><img src={sslImageSrc} alt='SSL Secure Connection' /></li>
                <li><img src={paypalImageSrc} alt='PayPal Verified' /></li>
                <li><img src={visaSecureImageSrc} alt='VISA Secure' /></li>
                <li><img src={mastercardIdCheckImageSrc} alt='Mastercard ID Check' /></li>
            </ul>
        </form>
    );
}

PaymentForm.propTypes = {
    className: PropTypes.string,
    successUrl: PropTypes.string,
    plan: PropTypes.string,
    discount: PropTypes.number,
    failedCard: PropTypes.shape({
        mask: PropTypes.string,
        numberOfFailedPayments: PropTypes.number
    }),
    isWalletEnabled: PropTypes.bool
};

PaymentForm.defaultProps = {
    className: '',
    successUrl: '',
    plan: '',
    discount: 0,
    isWalletEnabled: false
};

export {
    PaymentForm
};
